import { useCallback, useMemo, useState, type FC } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";

import { useAffiliateDeepLinkBaseUrl } from "../../../../hooks/useAffiliateDeepLinkBaseUrl";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { Button } from "../../../shared";

import CreatePromoCodeModal from "./CreatePromoCodeModal/CreatePromoCodeModal";
import type { CreatePromoCodeFormValues } from "./CreatePromoCodeModal/createPromoCodeModalData";
import PromoCodeListItem from "./PromoCodeListItem/PromoCodeListItem";
import {
  PROMO_CODE_NAME_MAX_LENGTH,
  buildAffiliateDeepLinkUrl,
  buildDuplicatePromoCodeName,
} from "./promoCodesData";

import styles from "./PromoCodes.module.css";

export type PromoCodesProps = {
  /**
   * Promo codes the affiliate has already created. The parent wizard owns
   * this list — `PromoCodes` is a controlled view. The next modal title is
   * derived as `promoCodes.length + 1` (`"Create Promo Code 1"`, `... 2`).
   */
  promoCodes?: ReadonlyArray<CreatePromoCodeFormValues>;
  /** Fired with the modal's form values when the user clicks `Save Code`. */
  onCreatePromoCode?: (values: CreatePromoCodeFormValues) => void;
  /**
   * Fired with the 0-based index and the edited values after the user
   * saves the modal in edit mode. The parent replaces the item in place.
   */
  onEditPromoCode?: (index: number, values: CreatePromoCodeFormValues) => void;
  /** Fired when the user clicks `Continue`. */
  onContinue?: () => void;
};

const PromoCodes: FC<PromoCodesProps> = ({
  promoCodes,
  onCreatePromoCode,
  onEditPromoCode,
  onContinue,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // `null` → modal will create a new code on submit.
  // `number` → modal will edit the code at that index on submit.
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // Source row whose fields seed the create modal when Duplicate falls
  // back to the modal path (see `handleDuplicateClick`). Routing the
  // overflow case through the same modal as create keeps the name-length
  // rule and any other field validations in a single place — bypassing
  // the modal here would let duplicates ship with names the create flow
  // itself would reject.
  const [duplicateSource, setDuplicateSource] =
    useState<CreatePromoCodeFormValues | null>(null);

  // Memoised so `handleDuplicateClick` (and any other callback that
  // closes over `items`) keeps a stable reference between renders when
  // the parent's `promoCodes` array hasn't changed identity.
  const items = useMemo(() => promoCodes ?? [], [promoCodes]);

  // The create modal derives `deepLinkUrl` from the watched `name`
  // through a `useEffect` (`buildAffiliateDeepLinkUrl(name, baseUrl)`).
  // The Duplicate fast path skips the modal, so we have to re-derive
  // here using the same host the modal uses — otherwise the source's
  // URL would tag along and the duplicate would point at the original
  // promo code on save (and in the wizard summary).
  const deepLinkBaseUrl = useAffiliateDeepLinkBaseUrl();

  const openCreateModal = useCallback(() => {
    setEditingIndex(null);
    setDuplicateSource(null);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingIndex(null);
    setDuplicateSource(null);
  }, []);

  const handleEditClick = useCallback(
    (index: number) => {
      // Only enter edit mode if the parent actually accepts edits — keeps
      // the Edit button from opening an empty modal whose save handler
      // would noop.
      if (!onEditPromoCode) return;
      setEditingIndex(index);
      setDuplicateSource(null);
      setIsModalOpen(true);
    },
    [onEditPromoCode],
  );

  const handleDuplicateClick = useCallback(
    (index: number) => {
      // Mirrors the Edit guard: don't seed a modal whose submit handler
      // is a noop because the parent hasn't wired up a create handler.
      if (!onCreatePromoCode) return;
      const source = items[index];
      if (!source) return;
      // Happy path: try the auto-numbered `" (N)"` shortcut. Because the
      // suffix grows monotonically with N, the lowest-free N already
      // gives the shortest possible duplicate; if that doesn't fit the
      // name-length cap, no other N would either. Fall back to opening
      // the create modal pre-filled with a blank name so the operator
      // picks one the validation accepts.
      const siblingNames = items.map((promoCode) => promoCode.name);
      const proposedName = buildDuplicatePromoCodeName(
        source.name,
        siblingNames,
      );
      if (proposedName.length <= PROMO_CODE_NAME_MAX_LENGTH) {
        onCreatePromoCode({
          ...source,
          name: proposedName,
          // Mirrors the modal's name→URL derivation. Without this, a
          // duplicate of `"Free 100"` (URL `…=FREE-100`) would ship
          // with the source's URL even though the new code is
          // `"Free 100 (1)"` — a silent referral-attribution bug.
          deepLinkUrl: buildAffiliateDeepLinkUrl(
            proposedName,
            deepLinkBaseUrl,
          ),
        });
        return;
      }
      setDuplicateSource({ ...source, name: "" });
      setEditingIndex(null);
      setIsModalOpen(true);
    },
    [deepLinkBaseUrl, items, onCreatePromoCode],
  );

  const handleModalSubmit = useCallback(
    (values: CreatePromoCodeFormValues) => {
      if (editingIndex !== null) {
        onEditPromoCode?.(editingIndex, values);
      } else {
        onCreatePromoCode?.(values);
      }
      setIsModalOpen(false);
      setEditingIndex(null);
      setDuplicateSource(null);
    },
    [editingIndex, onCreatePromoCode, onEditPromoCode],
  );

  const canContinue = items.length > 0;
  const isEditing = editingIndex !== null;
  const modalIndex = isEditing ? editingIndex + 1 : items.length + 1;
  const modalInitialValues = isEditing
    ? items[editingIndex]
    : (duplicateSource ?? undefined);

  return (
    <section
      className={styles.container}
      data-testid="new-affiliate-promo-codes"
      aria-labelledby="new-affiliate-promo-codes-title"
    >
      <div className={styles.titleRow}>
        <h2 id="new-affiliate-promo-codes-title" className={styles.title}>
          {AppStrings.NEW_AFFILIATE_PROMO_CODES}
        </h2>
        <Tooltip title={AppStrings.NEW_AFFILIATE_PROMO_CODES_TOOLTIP}>
          <InfoCircleOutlined
            className={styles.titleInfoIcon}
            aria-label={AppStrings.NEW_AFFILIATE_PROMO_CODES_INFO_LABEL}
            data-testid="new-affiliate-promo-codes-info"
          />
        </Tooltip>
      </div>

      {items.length > 0 && (
        <ul
          className={styles.list}
          data-testid="new-affiliate-promo-codes-list"
        >
          {items.map((promoCode, idx) => (
            <PromoCodeListItem
              // Using the 1-based index as key is acceptable here because the
              // list is parent-owned and edits replace items in place — no
              // reordering occurs.
              key={idx + 1}
              index={idx + 1}
              promoCode={promoCode}
              onEdit={
                onEditPromoCode ? () => handleEditClick(idx) : undefined
              }
              onDuplicate={
                onCreatePromoCode
                  ? () => handleDuplicateClick(idx)
                  : undefined
              }
            />
          ))}
        </ul>
      )}

      <div className={styles.actions}>
        <Button
          intent="primary"
          onClick={openCreateModal}
          data-testid="new-affiliate-create-promo-code"
        >
          {AppStrings.NEW_AFFILIATE_CREATE_PROMO_CODE}
        </Button>
      </div>

      <div className={styles.divider} role="separator" aria-hidden="true" />

      <div className={styles.footer}>
        <Button
          intent="primary"
          onClick={onContinue}
          disabled={!canContinue}
          data-testid="new-affiliate-promo-codes-continue"
        >
          {AppStrings.CONTINUE}
        </Button>
      </div>

      <CreatePromoCodeModal
        open={isModalOpen}
        mode={isEditing ? "edit" : "create"}
        index={modalIndex}
        initialValues={modalInitialValues}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
      />
    </section>
  );
};

export default PromoCodes;
